import type {ComponentProps, Key} from 'react';
import {useMenuSection} from '@react-aria/menu';
import {useSeparator} from '@react-aria/separator';
import type {TreeState} from '@react-stately/tree';
import type {CollectionChildren, Node} from '@react-types/shared';

import {MenuItem} from './menu-item';
import {MenuItemWithSubMenu} from './menu-item-with-submenu';
import styles from './menu-section.module.css';

type Props = {
    section: Node<object>;
    state: TreeState<object>;
    onAction?: (key: Key) => void;
    onClose?: () => void;
    subMenus?: Record<Key, CollectionChildren<object>>;
};

export const MenuSection = ({section, state, onAction, onClose, subMenus}: Props) => {
    const {itemProps: sectionProps, groupProps} = useMenuSection({
        heading: section.rendered,
        'aria-label': section['aria-label'],
    });

    const {separatorProps} = useSeparator({
        elementType: 'li',
    });

    return (
        <>
            {section.key !== state.collection.getFirstKey() && <li {...separatorProps} className={styles.separator} />}
            <li {...sectionProps} className={styles.sectionRoot}>
                <ul {...groupProps} className={styles.sectionItems}>
                    {[...section.childNodes].map((node) => {
                        const hasSubMenu = Boolean(subMenus?.[node.key]);
                        const itemProps: ComponentProps<typeof MenuItem> = {
                            key: node.key,
                            item: node,
                            state,
                            onAction,
                            onClose,
                        };

                        return hasSubMenu ? (
                            <MenuItemWithSubMenu {...itemProps} subMenuItems={subMenus![node.key] as CollectionChildren<object>} />
                        ) : (
                            <MenuItem {...itemProps} />
                        );
                    })}
                </ul>
            </li>
        </>
    );
};
